import axios from "axios";
import { tokenUtils } from "../hooks/useTokenStorage";

// In-memory cache for tokens to avoid repeated localStorage access
// This improves performance for the interceptors
let accessToken = null;
let refreshToken = null;
let refreshInProgress = null;

// Wrapper functions that use tokenUtils and maintain in-memory cache
const setTokens = (tokens) => {
  tokenUtils.setTokens(tokens);
  if (tokens.accessToken) {
    accessToken = tokens.accessToken;
  }
  if (tokens.refreshToken) {
    refreshToken = tokens.refreshToken;
  }
};

const clearTokens = () => {
  tokenUtils.clearTokens();
  accessToken = null;
  refreshToken = null;
};

const isTokenExpired = () => {
  return tokenUtils.isTokenExpired();
};

const createApi = () => {
  const apiUrl =
    (typeof window !== "undefined" ? window.env?.REACT_APP_API_URL : undefined) ||
    process.env.REACT_APP_API_URL;
  const isDebug =
    (typeof window !== "undefined"
      ? window.env?.REACT_APP_DEBUG === "true"
      : false) || process.env.REACT_APP_DEBUG === "true";

  if (isDebug && process.env.NODE_ENV === "development") {
    console.log("[API DEBUG] Environment variables:", {
      window_env:
        typeof window !== "undefined" ? window.env : { available: false },
      REACT_APP_API_URL: apiUrl,
      NODE_ENV: process.env.NODE_ENV,
    });

    console.log("[API DEBUG] Creating API client with config:", {
      baseURL: apiUrl,
      env: process.env.NODE_ENV,
    });
  }

  const api = axios.create({
    baseURL: apiUrl,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
    xsrfCookieName: "XSRF-TOKEN",
    xsrfHeaderName: "X-XSRF-TOKEN",
  });

  // リクエストインターセプター
  if (api.interceptors) {
    api.interceptors.request.use(
      async (config) => {
        if (
          accessToken &&
          isTokenExpired() &&
          refreshToken &&
          !config.url.includes("/refresh-token") &&
          !config.url.includes("/login")
        ) {
          try {
            if (!refreshInProgress) {
              refreshInProgress = refreshAccessToken(api);
            }

            await refreshInProgress;
            refreshInProgress = null;
          } catch (error) {
            console.error("[API DEBUG] Token refresh failed:", error);
            clearTokens();
            refreshInProgress = null;

            // 公開エンドポイントの場合はログイン画面にリダイレクトせず、
            // トークンなしでリクエストを続行する
            if (config.url.includes("/public/") || config.url.includes("/qr/")) {
              console.log("[API DEBUG] Proceeding without token for public endpoint");
              if (config.headers.Authorization) {
                delete config.headers.Authorization;
              }
              return config;
            }

            window.location.href = "/login";
            return Promise.reject(error);
          }
        }

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        if (isDebug) {
          console.log("[API DEBUG] Request:", {
            url: config.url,
            method: config.method,
            baseURL: config.baseURL,
            headers: config.headers,
            withCredentials: config.withCredentials,
          });
        }
        return config;
      },
      (error) => {
        console.error("[API DEBUG] Request error:", error);
        return Promise.reject(error);
      }
    );

    // レスポンスインターセプター
    api.interceptors.response.use(
      (response) => {
        if (isDebug) {
          console.log("[API DEBUG] Response:", {
            status: response.status,
            headers: response.headers,
            data: response.data,
          });
        }
        // 正規化: id/_id 両対応、snake_case -> camelCase
        try {
          const normalizeKeys = (obj) => {
            if (Array.isArray(obj)) return obj.map(normalizeKeys);
            if (obj && typeof obj === "object") {
              const out = {};
              for (const [k, v] of Object.entries(obj)) {
                const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                out[camel] = normalizeKeys(v);
              }
              if (out.id && !out._id) out._id = out.id;
              if (out._id && !out.id) out.id = out._id;
              return out;
            }
            return obj;
          };
          response.data = normalizeKeys(response.data);
        } catch {}
        return response;
      },
      async (error) => {
        if (isDebug) {
          console.error("[API DEBUG] Response error:", {
            status: error.response?.status,
            data: error.response?.data,
            config: error.config,
            message: error.message,
          });
        }

        if (error.response?.status === 401) {
          if (
            error.response.data?.code === "TOKEN_EXPIRED" &&
            refreshToken &&
            !error.config.url.includes("/refresh-token")
          ) {
            try {
              if (!refreshInProgress) {
                refreshInProgress = refreshAccessToken(api);
              }

              await refreshInProgress;
              refreshInProgress = null;

              const originalRequest = error.config;
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return api(originalRequest);
            } catch (refreshError) {
              console.error("[API DEBUG] Token refresh failed:", refreshError);
              clearTokens();
              refreshInProgress = null;
            }
          } else {
            clearTokens();
            
            // 公開エンドポイントの場合はトークンなしで再試行
            if (error.config.url.includes("/public/") || error.config.url.includes("/qr/")) {
              console.log("[API DEBUG] Retrying public endpoint without token");
              const originalRequest = error.config;
              if (originalRequest.headers.Authorization) {
                delete originalRequest.headers.Authorization;
              }
              return api(originalRequest);
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  return api;
};

const refreshAccessToken = async (api) => {
  try {
    const storedRefreshToken =
      tokenUtils.getRefreshToken() || refreshToken;

    if (!storedRefreshToken) {
      throw new Error("リフレッシュトークンがありません");
    }

    const response = await api.post("/api/auth/refresh-token", {
      refreshToken: storedRefreshToken,
    });

    const {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiresIn,
    } = response.data;

    setTokens({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiresIn,
    });

    return newAccessToken;
  } catch (error) {
    console.error("[API DEBUG] Failed to refresh token:", error);
    clearTokens();
    throw error;
  }
};

// Initialize in-memory token cache from localStorage on app startup
const initializeTokens = () => {
  accessToken = tokenUtils.getAccessToken();
  refreshToken = tokenUtils.getRefreshToken();
};

initializeTokens();

const api = createApi();

export default api;
export {
  createApi,
  setTokens,
  clearTokens,
  isTokenExpired,
  refreshAccessToken,
};
